import { motion } from 'framer-motion';
import {
  ShieldCheck,
  AlertTriangle,
  AlertCircle,
  MapPin,
  Gauge,
  Target,
  Users,
  Home as HomeIcon,
  CheckCircle,
  Lock,
  Brain,
  LineChart,
  FileText,
  Sparkles,
  ChevronDown,
  Calculator,
  TrendingUp,
  Activity,
  DollarSign,
  Clock,
  Infinity,
  BarChart,
  BarChart2,
  Sliders
} from 'lucide-react';
import { useState } from 'react';
import { CTAButton } from '../components/landing/CTAButton';
import { SectionTitle } from '../components/landing/SectionTitle';
import { FeatureCard } from '../components/landing/FeatureCard';
import { Badge } from '../components/landing/Badge';

export default function Home() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">

      {/* SECTION 1: HERO */}
      <section className="relative py-20 lg:py-24 px-4">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 to-slate-900/20" />
       <div className="max-w-6xl mx-auto relative z-10">
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.8 }}
  >
    {/* Badge calé à gauche */}
<div className="text-center lg:text-left mb-8">
  <Badge icon={ShieldCheck} text="Données certifiées data.gouv.fr" variant="blue" />
</div>

    {/* Layout principal : texte à gauche + score à droite */}
    <div className="flex flex-col lg:flex-row items-center lg:items-start justify-between gap-12">
      
      {/* Partie gauche : TOUT le texte + CTA */}
      <div className="flex-1 text-center lg:text-left">
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
          Votre logement est-il suffisamment sécurisé ?
        </h1>

        <p className="text-xl md:text-2xl text-slate-300 mb-8">
          Score gratuit en 5 minutes • Tableau de bord de pilotage inclus
        </p>

<div className="mb-12">
  {/* Bouton seul avec alignement */}
  <div className="flex justify-center lg:justify-start mb-6">
    <CTAButton
      variant="primary"
      size="xl"
      icon
    >
      Commencer mon diagnostic gratuit
    </CTAButton>
  </div>
  
  {/* Textes en dessous avec alignement */}
  <div className="space-y-2 text-center lg:text-left">
    <p className="text-sm text-blue-200">
      Score gratuit instantané • 5 minutes • Sans inscription
    </p>
    <p className="text-sm text-green-300 flex items-center justify-center lg:justify-start gap-2">
      <CheckCircle className="w-4 h-4" />
      Aucune carte bancaire requise pour le score gratuit
    </p>
  </div>
        </div>
      </div>

      {/* Partie droite : Score circulaire */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.4, duration: 0.8 }}
        className="flex-shrink-0"
      >
        <div className="inline-block p-8 bg-gradient-to-br from-slate-800/60 to-slate-900/60 backdrop-blur-xl border border-white/10 rounded-3xl shadow-2xl">
          <div className="relative w-48 h-48 mx-auto">
            <svg width="192" height="192" style={{ transform: 'rotate(-90deg)' }}>
              <defs>
                <linearGradient id="scoreGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" style={{ stopColor: '#3b82f6' }} />
                  <stop offset="100%" style={{ stopColor: '#2563eb' }} />
                </linearGradient>
              </defs>
              <circle
                cx="96"
                cy="96"
                r="85"
                fill="none"
                stroke="rgba(255,255,255,0.05)"
                strokeWidth="10"
              />
              <motion.circle
                cx="96"
                cy="96"
                r="85"
                fill="none"
                stroke="url(#scoreGradient)"
                strokeWidth="10"
                strokeLinecap="round"
                strokeDasharray={534}
                initial={{ strokeDashoffset: 534 }}
                animate={{ strokeDashoffset: 143 }}
                transition={{ duration: 2, ease: "easeOut" }}
              />
            </svg>
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center">
              <span className="text-6xl font-bold text-white">73</span>
              <span className="text-2xl text-slate-400">/100</span>
              <p className="text-sm text-slate-400 mt-2">Score DPS</p>
            </div>
          </div>
        </div>
      </motion.div>

    </div>
  </motion.div>
</div>
      </section>
        
      {/* SECTION 2: PROBLÈME + AGITATION */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <SectionTitle
            title="Pourquoi 83% des propriétaires ne savent pas si leur logement est vraiment sécurisé"
          />

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <FeatureCard
              icon={AlertTriangle}
              iconColor="#ef4444"
              title="Vous investissez à l'aveugle dans la sécurité"
              description="Vous ne savez pas si votre porte blindée compense l'absence d'alarme, ni comment prioriser vos investissements"
              delay={0}
            />
            <FeatureCard
              icon={AlertCircle}
              iconColor="#f59e0b"
              title="Recommandations non personnalisées"
              description="Les conseils trouvés en ligne ne tiennent pas compte de VOTRE architecture, VOTRE quartier, ni des cambriolages dans VOTRE commune"
              delay={0.1}
            />
            <FeatureCard
              icon={MapPin}
              iconColor="#fbbf24"
              title="Vous ignorez le véritable danger de votre quartier"
              description="Vous ignorez le taux réel de cambriolages dans votre rue, votre ville, votre département"
              delay={0.2}
            />
          </div>

          <div className="text-center">
            <CTAButton variant="outline" size="lg">
              Oui, je veux savoir où j'en suis
            </CTAButton>
          </div>
        </div>
      </section>

      {/* SECTION 3: SOLUTION (GRAND SLAM OFFER) */}
      <section className="py-20 px-4 bg-slate-900/50">
        <div className="max-w-6xl mx-auto">
          <SectionTitle
            title="Le seul diagnostic qui analyse 50+ paramètres spécifiques à votre logement"
            subtitle="Contrairement aux checklists génériques, notre algorithme intègre votre architecture, vos équipements, votre environnement ET les statistiques de cambriolages de votre commune"
          />

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <FeatureCard
              icon={Gauge}
              iconColor="#3b82f6"
              title="Score sur 100"
              description=""
              list={[
                "Score global de sécurité",
                "Comparaison avec moyenne nationale",
                "Positionnement par type de logement"
              ]}
              delay={0}
            />
            <FeatureCard
              icon={ShieldCheck}
              iconColor="#8b5cf6"
              title="Données officielles intégrées"
              description=""
              list={[
                "Statistiques de cambriolages de VOTRE commune",
                "Comparaison départementale et nationale",
                "Taux pour 1000 logements",
                "Niveau de risque calculé (high/medium/low)"
              ]}
              delay={0.1}
            />
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="
                bg-gradient-to-br from-slate-800/40 to-slate-900/40
                backdrop-blur-xl
                border border-white/10
                rounded-2xl
                p-8
                hover:border-white/20
                transition-all duration-300
                hover:shadow-2xl
              "
            >
              <div className="mb-6">
                <div className="flex items-center gap-3">
                  <div
                    className="inline-flex items-center justify-center w-14 h-14 rounded-xl"
                    style={{ backgroundColor: '#ec489920' }}
                  >
                    <Target size={28} color="#ec4899" />
                  </div>
                  <Badge text="PREMIUM" variant="blue" />
                </div>
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">
                Recommandations priorisées
              </h3>
              <p className="text-slate-300 leading-relaxed mb-4">
              </p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2 text-slate-300 text-sm">
                  <span className="text-blue-400 mt-1">✓</span>
                  <span>3 catégories détaillées (Protection/Surveillance/Dissuasion)</span>
                </li>
                <li className="flex items-start gap-2 text-slate-300 text-sm">
                  <span className="text-blue-400 mt-1">✓</span>
                  <span>15-30 actions personnalisées</span>
                </li>
                <li className="flex items-start gap-2 text-slate-300 text-sm">
                  <span className="text-blue-400 mt-1">✓</span>
                  <span>Coût estimé par amélioration</span>
                </li>
                <li className="flex items-start gap-2 text-slate-300 text-sm">
                  <span className="text-blue-400 mt-1">✓</span>
                  <span>Impact exact sur votre score (+X points)</span>
                </li>
                <li className="flex items-start gap-2 text-slate-300 text-sm">
                  <span className="text-blue-400 mt-1">✓</span>
                  <span>Tri automatique par priorité</span>
                </li>
              </ul>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="
                bg-gradient-to-br from-slate-800/40 to-slate-900/40
                backdrop-blur-xl
                border border-white/10
                rounded-2xl
                p-8
                hover:border-white/20
                transition-all duration-300
                hover:shadow-2xl
              "
            >
              <div className="mb-6">
                <div className="flex items-center gap-3">
                  <div
                    className="inline-flex items-center justify-center w-14 h-14 rounded-xl"
                    style={{ backgroundColor: '#ec489920' }}
                  >
                    <Activity size={28} color="#ec4899" />
                  </div>
                  <Badge text="PREMIUM" variant="blue" />
                </div>
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">
                Outil de pilotage continu
              </h3>
              <p className="text-slate-300 leading-relaxed mb-4">
              </p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2 text-slate-300 text-sm">
                  <span className="text-blue-400 mt-1">✓</span>
                  <span>Mise à jour du score après chaque action</span>
                </li>
                <li className="flex items-start gap-2 text-slate-300 text-sm">
                  <span className="text-blue-400 mt-1">✓</span>
                  <span>Graphique d'évolution temporelle</span>
                </li>
                <li className="flex items-start gap-2 text-slate-300 text-sm">
                  <span className="text-blue-400 mt-1">✓</span>
                  <span>Comparaison avec milliers d'utilisateurs</span>
                </li>
                <li className="flex items-start gap-2 text-slate-300 text-sm">
                  <span className="text-blue-400 mt-1">✓</span>
                  <span>Accès illimité à votre dashboard</span>
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SECTION 4: COMMENT ÇA MARCHE */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <SectionTitle
            title="3 étapes, 5 minutes, score immédiat"
          />

          <div className="space-y-8">
            {[
              {
                number: 1,
                duration: "2 minutes",
                title: "Caractéristiques",
                description: "Type de logement, nombre d'étages, dépendances, environnement",
                icon: HomeIcon,
              },
              {
                number: 2,
                duration: "30 secondes",
                title: "Localisation",
                description: "Votre adresse → récupération automatique des statistiques de cambriolages locales",
                icon: MapPin,
              },
              {
                number: 3,
                duration: "2 minutes",
                title: "Équipements",
                description: "Sélection de vos équipements actuels parmi 27 catalogués (alarme, caméras, porte blindée...)",
                icon: ShieldCheck,
              },
            ].map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                className="flex gap-6 items-start"
              >
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-2xl font-bold shadow-lg">
                    {step.number}
                  </div>
                </div>
                <div className="flex-grow bg-gradient-to-br from-slate-800/40 to-slate-900/40 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <step.icon size={24} className="text-blue-400" />
                    <h3 className="text-xl font-semibold text-white">{step.title}</h3>
                    <span className="ml-auto text-sm text-blue-400 bg-blue-500/20 px-3 py-1 rounded-full">
                      {step.duration}
                    </span>
                  </div>
                  <p className="text-slate-300 leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12 p-6 bg-gradient-to-r from-green-500/20 to-blue-500/20 border border-green-500/30 rounded-2xl text-center"
          >
            <div className="flex items-center justify-center gap-3 text-green-300">
              <Sparkles size={28} />
              <p className="text-lg font-semibold">
                Score instantané + statistiques locales GRATUITEMENT
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SECTION 3bis: OUTIL DE PILOTAGE CONTINU (PREMIUM) */}
      <section className="py-20 px-4 bg-slate-900/50">
        <div className="max-w-6xl mx-auto">
          <SectionTitle
            title="Avec le rapport premium : ne vous arrêtez pas au diagnostic"
            subtitle="Pour 9,90€, transformez votre diagnostic en véritable outil de pilotage sur le long terme"
          />

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {/* Colonne 1: Simulateur d'impact */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0 }}
              className="bg-gradient-to-br from-slate-800/40 to-slate-900/40 backdrop-blur-xl border border-white/10 rounded-2xl p-8 hover:border-white/20 transition-all duration-300"
            >
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <div
                    className="inline-flex items-center justify-center w-14 h-14 rounded-xl"
                    style={{ backgroundColor: '#3b82f620' }}
                  >
                    <Calculator size={28} color="#3b82f6" />
                  </div>
                  <Badge text="PREMIUM" variant="blue" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">
                  Testez avant d'investir
                </h3>
                <p className="text-slate-300 leading-relaxed mb-6">
                  Cochez les équipements qui vous intéressent → voyez instantanément l'impact sur votre score et le budget total nécessaire. Comparez 3 gammes de prix par amélioration.
                </p>
              </div>

              {/* Mockup du simulateur */}
              <div className="bg-gradient-to-br from-blue-500/10 to-blue-600/10 border border-blue-500/30 rounded-xl p-4">
                <div className="bg-blue-500/20 border border-blue-500/40 rounded-lg p-3 mb-3">
                  <div className="text-center">
                    <p className="text-xs text-blue-300 mb-1">Score actuel: 65 → Score projeté: 78</p>
                    <p className="text-2xl font-bold text-blue-400">+13 points</p>
                  </div>
                </div>
                <div className="space-y-2 text-xs text-slate-400">
                  <div className="flex items-center gap-2">
                    <CheckCircle size={14} className="text-blue-400" />
                    <span>Installation alarme</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle size={14} className="text-blue-400" />
                    <span>Caméras extérieures</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle size={14} className="text-blue-400" />
                    <span>Éclairage automatique</span>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-blue-500/30 text-center">
                  <p className="text-sm font-semibold text-white">Budget estimé : 1 240€</p>
                </div>
              </div>
            </motion.div>

            {/* Colonne 2: Suivi dans le temps */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-gradient-to-br from-slate-800/40 to-slate-900/40 backdrop-blur-xl border border-white/10 rounded-2xl p-8 hover:border-white/20 transition-all duration-300"
            >
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <div
                    className="inline-flex items-center justify-center w-14 h-14 rounded-xl"
                    style={{ backgroundColor: '#10b98120' }}
                  >
                    <TrendingUp size={28} color="#10b981" />
                  </div>
                  <Badge text="PREMIUM" variant="blue" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">
                  Suivez votre progression
                </h3>
                <p className="text-slate-300 leading-relaxed mb-6">
                  Marquez vos actions comme réalisées → votre score se met à jour automatiquement. Graphique d'évolution inclus avec snapshots de chaque amélioration.
                </p>
              </div>

              {/* Mockup du graphique */}
              <div className="bg-gradient-to-br from-green-500/10 to-green-600/10 border border-green-500/30 rounded-xl p-4">
                <div className="relative h-32 mb-3">
                  <svg className="w-full h-full" viewBox="0 0 200 80">
                    <polyline
                      points="10,60 60,45 110,35 160,20"
                      fill="none"
                      stroke="#10b981"
                      strokeWidth="2"
                    />
                    <circle cx="10" cy="60" r="3" fill="#10b981" />
                    <circle cx="60" cy="45" r="3" fill="#10b981" />
                    <circle cx="110" cy="35" r="3" fill="#10b981" />
                    <circle cx="160" cy="20" r="3" fill="#10b981" />
                  </svg>
                  <div className="absolute bottom-0 left-0 right-0 flex justify-between text-xs text-slate-500">
                    <span>Jan</span>
                    <span>Fév</span>
                    <span>Mar</span>
                    <span>Avr</span>
                  </div>
                </div>
                <div className="space-y-1 text-xs text-slate-400">
                  <p>• Jan 2025 — Initial: 58pts</p>
                  <p>• Fév 2025 — Alarme: +8pts</p>
                  <p>• Mar 2025 — Caméras: +5pts</p>
                  <p className="text-green-400 font-semibold">• Avr 2025 — Actuel: 73pts</p>
                </div>
              </div>
            </motion.div>

            {/* Colonne 3: Positionnement relatif */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-gradient-to-br from-slate-800/40 to-slate-900/40 backdrop-blur-xl border border-white/10 rounded-2xl p-8 hover:border-white/20 transition-all duration-300"
            >
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <div
                    className="inline-flex items-center justify-center w-14 h-14 rounded-xl"
                    style={{ backgroundColor: '#8b5cf620' }}
                  >
                    <Users size={28} color="#8b5cf6" />
                  </div>
                  <Badge text="PREMIUM" variant="blue" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">
                  Comparez-vous anonymement
                </h3>
                <p className="text-slate-300 leading-relaxed mb-6">
                  Découvrez votre percentile vs milliers d'autres propriétaires. Êtes-vous dans le top 25% des logements sécurisés ? Moyenne par type de logement incluse.
                </p>
              </div>

              {/* Mockup de la comparaison */}
              <div className="bg-gradient-to-br from-purple-500/10 to-purple-600/10 border border-purple-500/30 rounded-xl p-4">
                <div className="mb-4">
                  <div className="relative h-8 bg-gradient-to-r from-red-500/30 via-yellow-500/30 to-green-500/30 rounded-full overflow-hidden">
                    <div className="absolute left-3/4 top-0 bottom-0 w-1 bg-white"></div>
                    <div className="absolute left-3/4 -top-6 text-xs text-white whitespace-nowrap transform -translate-x-1/2">
                      Vous ↓
                    </div>
                  </div>
                  <p className="text-center text-sm font-semibold text-purple-300 mt-3">
                    75e percentile
                  </p>
                  <p className="text-center text-xs text-slate-400">
                    Meilleur que 75% des logements
                  </p>
                </div>
                <div className="space-y-2 text-xs text-slate-400 pt-3 border-t border-purple-500/30">
                  <div className="flex justify-between">
                    <span>Moyenne globale:</span>
                    <span className="text-white font-semibold">58/100</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Moyenne maisons:</span>
                    <span className="text-white font-semibold">54/100</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Moyenne appartements:</span>
                    <span className="text-white font-semibold">62/100</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          <div className="text-center">
            <CTAButton
              variant="primary"
              size="lg"
              href="/diagnostic"
              subtext="Paiement unique • Accès à vie"
            >
              Débloquer mon outil de pilotage — 9,90€
            </CTAButton>
          </div>
        </div>
      </section>

      {/* SECTION 5: PREUVE SOCIALE + CRÉDIBILITÉ */}
      <section className="py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <SectionTitle
            title="Basé sur des données officielles et un algorithme éprouvé"
          />

          <div className="grid md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-slate-800/40 to-slate-900/40 backdrop-blur-xl border border-white/10 rounded-2xl p-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <ShieldCheck size={32} className="text-blue-400" />
                <h3 className="text-2xl font-semibold text-white">Sources officielles</h3>
              </div>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-blue-400 rounded-full" />
                  <span className="text-slate-300">Ministère de l'Intérieur</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-blue-400 rounded-full" />
                  <span className="text-slate-300">data.gouv.fr</span>
                </div>
                <p className="text-slate-400 text-sm leading-relaxed mt-4">
                  Statistiques de cambriolages 2023-2024 certifiées par le Ministère de l'Intérieur
                </p>
                <div className="mt-4 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                  <p className="text-blue-300 font-medium text-center">
                    3,95 cambriolages pour 1000 logements = moyenne nationale officielle
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-slate-800/40 to-slate-900/40 backdrop-blur-xl border border-white/10 rounded-2xl p-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <Brain size={32} className="text-purple-400" />
                <h3 className="text-2xl font-semibold text-white">Algorithme transparent</h3>
              </div>
              <ul className="space-y-3">
                {[
                  "50+ paramètres analysés",
                  "Rendements décroissants anti-suréquipement",
                  "Pondération contextuelle selon risque local",
                  "6 niveaux de multiplicateurs",
                  "Plafond strict à 95/100 (perfection impossible = honnêteté)"
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3 text-slate-300">
                    <CheckCircle size={20} className="text-purple-400 flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SECTION 5bis: GESTION DE PROJET SÉCURITÉ (PREMIUM) */}
      <section className="py-20 px-4 bg-slate-900/50">
        <div className="max-w-6xl mx-auto">
          <SectionTitle
            title="Avec le rapport premium : gérez vos améliorations comme un projet professionnel"
            subtitle="Un vrai outil de gestion, pas une simple liste de recommandations"
          />

          <div className="space-y-6 mb-12">
            {/* Bénéfice 1: Planification budgétaire */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0 }}
              className="bg-gradient-to-br from-slate-800/40 to-slate-900/40 backdrop-blur-xl border border-white/10 rounded-2xl p-8"
            >
              <div className="flex items-start gap-6">
                <div
                  className="flex-shrink-0 w-14 h-14 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: '#10b98120' }}
                >
                  <DollarSign size={28} color="#10b981" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-3">
                    Planification budgétaire précise
                  </h3>
                  <p className="text-slate-300 leading-relaxed">
                    15 à 30 actions personnalisées avec coûts estimés pour chacune. 3 gammes de prix (Budget/Recommandé/Premium) pour adapter à votre budget.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Bénéfice 2: Priorisation automatique */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-gradient-to-br from-slate-800/40 to-slate-900/40 backdrop-blur-xl border border-white/10 rounded-2xl p-8"
            >
              <div className="flex items-start gap-6">
                <div
                  className="flex-shrink-0 w-14 h-14 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: '#3b82f620' }}
                >
                  <Target size={28} color="#3b82f6" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-3">
                    Priorisation automatique intelligente
                  </h3>
                  <p className="text-slate-300 leading-relaxed">
                    Tri automatique par priorité (haute/moyenne/basse) + calcul du meilleur ratio points gagnés par euro investi. Commencez par ce qui compte vraiment.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Bénéfice 3: Gestion de statut avancée */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-gradient-to-br from-slate-800/40 to-slate-900/40 backdrop-blur-xl border border-white/10 rounded-2xl p-8"
            >
              <div className="flex items-start gap-6">
                <div
                  className="flex-shrink-0 w-14 h-14 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: '#8b5cf620' }}
                >
                  <CheckCircle size={28} color="#8b5cf6" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-3">
                    Gestion de statut avancée
                  </h3>
                  <p className="text-slate-300 leading-relaxed">
                    Workflow complet : Suggéré → Planifié → En cours → Réalisé. Enregistrez le coût réel et vos notes d'implémentation pour chaque action.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Bénéfice 4: ROI sécuritaire */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="bg-gradient-to-br from-slate-800/40 to-slate-900/40 backdrop-blur-xl border border-white/10 rounded-2xl p-8"
            >
              <div className="flex items-start gap-6">
                <div
                  className="flex-shrink-0 w-14 h-14 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: '#ec489920' }}
                >
                  <TrendingUp size={28} color="#ec4899" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-3">
                    ROI sécuritaire calculé
                  </h3>
                  <p className="text-slate-300 leading-relaxed">
                    Visualisez combien de points de sécurité vous gagnez par 1000€ investis. Justifiez rationnellement chaque investissement.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Bénéfice 5: Historique complet */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="bg-gradient-to-br from-slate-800/40 to-slate-900/40 backdrop-blur-xl border border-white/10 rounded-2xl p-8"
            >
              <div className="flex items-start gap-6">
                <div
                  className="flex-shrink-0 w-14 h-14 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: '#f59e0b20' }}
                >
                  <Clock size={28} color="#f59e0b" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-3">
                    Historique complet traçable
                  </h3>
                  <p className="text-slate-300 leading-relaxed">
                    Timeline de toutes vos actions avec dates, montants et gains de points. Prouvez l'amélioration de votre sécurité dans le temps.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Bénéfice 6: Accès illimité */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
              className="bg-gradient-to-br from-slate-800/40 to-slate-900/40 backdrop-blur-xl border border-white/10 rounded-2xl p-8"
            >
              <div className="flex items-start gap-6">
                <div
                  className="flex-shrink-0 w-14 h-14 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: '#06b6d420' }}
                >
                  <Infinity size={28} color="#06b6d4" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-3">
                    Accès illimité à vie
                  </h3>
                  <p className="text-slate-300 leading-relaxed">
                    Revenez quand vous voulez. Votre tableau de bord reste accessible pour toujours. Mettez à jour votre score après chaque nouvelle amélioration.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Encart de mise en avant */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-blue-500/20 to-blue-600/20 border-2 border-blue-500/40 rounded-2xl p-8 text-center"
          >
            <p className="text-xl text-blue-200 font-semibold">
              Suivez la sécurisation de votre bien comme vous suivriez des travaux de rénovation
            </p>
          </motion.div>
        </div>
      </section>

      {/* SECTION 6: VALUE STACK */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <SectionTitle
            title="Débloquez votre rapport complet pour seulement 9,90€"
            subtitle="Paiement unique • Accès à vie • Aucun abonnement"
          />

          <div className="space-y-4 mb-8">
            {[
              {
                title: "Dashboard de pilotage interactif",
                description: "3 scores détaillés + évolution dans le temps • Suivi actions planifiées/en cours/réalisées • Calcul automatique du ROI sécuritaire",
                value: "12€",
                icon: BarChart,
              },
              {
                title: "Analyse IA contextuelle",
                description: "Insights personnalisés (OpenAI) • Synthèse executive unique • Recommandations adaptées",
                value: "9€",
                icon: Brain,
              },
              {
                title: "Simulateur d'investissements",
                description: "Testez différents scénarios avant d'acheter • Calcul instantané : budget total + gain de points • Comparaison 3 gammes de prix par équipement",
                value: "9€",
                icon: Sliders,
              },
              {
                title: "Cartographie vulnérabilités",
                description: "0 à 5 points faibles identifiés • Recommandations spécifiques • Priorisation automatique",
                value: "7€",
                icon: Target,
              },
              {
                title: "Historique et progression",
                description: "Timeline complète de vos améliorations • Graphique d'évolution du score • Snapshots automatiques à chaque action",
                value: "7€",
                icon: LineChart,
              },
              {
                title: "Export PDF professionnel",
                description: "Rapport téléchargeable • Multi-pages avec branding • Imprimable et partageable",
                value: "6€",
                icon: FileText,
              },
              {
                title: "Comparaison anonyme",
                description: "Votre percentile vs base utilisateurs • Position dans le classement national • Moyenne par type de logement (maison/appartement)",
                value: "6€",
                icon: BarChart2,
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-gradient-to-r from-slate-800/60 to-slate-900/60 backdrop-blur-xl border border-white/10 rounded-xl p-6 hover:border-white/20 transition-all"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4 flex-grow">
                    <div className="w-12 h-12 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                      <item.icon size={24} className="text-blue-400" />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-white mb-2">{item.title}</h4>
                      <p className="text-sm text-slate-400">{item.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-slate-500 line-through">{item.value}</span>
                    <CheckCircle size={20} className="text-green-400" />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-green-500/20 to-blue-500/20 border-2 border-green-500/40 rounded-2xl p-8 text-center mb-8"
          >
            <div className="flex items-center justify-center gap-4 flex-wrap mb-4">
              <div>
                <p className="text-sm text-slate-400">Valeur totale</p>
                <p className="text-3xl font-bold text-slate-300 line-through">56€</p>
              </div>
              <div className="text-4xl text-slate-500">→</div>
              <div>
                <p className="text-sm text-green-400">Votre prix</p>
                <p className="text-5xl font-bold text-white">9,90€</p>
              </div>
            </div>
            <p className="text-green-300 text-xl font-semibold">
              Économie de 46€ (82%)
            </p>
          </motion.div>

          <div className="text-center">
            <CTAButton
              variant="primary"
              size="xl"
              icon
              href="/diagnostic"
              subtext="Paiement sécurisé Stripe • Accès immédiat"
            >
              Générer mon rapport complet — 9,90€
            </CTAButton>
          </div>
        </div>
      </section>

      {/* SECTION 7: FAQ */}
      <section className="py-20 px-4 bg-slate-900/50">
        <div className="max-w-3xl mx-auto">
          <SectionTitle title="Questions fréquentes" />

          <div className="space-y-4">
            {[
              {
                question: "Mes données personnelles sont-elles protégées ?",
                answer: "Oui, 100%. Nous ne demandons aucun nom/prénom. Votre adresse sert uniquement au calcul statistique local et n'est jamais partagée. Comparaison anonyme garantie."
              },
              {
                question: "C'est vraiment basé sur des données officielles ?",
                answer: "Oui. Nous utilisons l'API data.gouv.fr qui centralise les statistiques du Ministère de l'Intérieur. Vous pouvez vérifier la source directement sur les pages de résultats."
              },
              {
                question: "Pourquoi 9,90€ et pas gratuit ?",
                answer: "La version gratuite vous donne votre score global et les stats locales. Le rapport premium ajoute 10 sections interactives (simulateur, IA, suivi dans le temps, PDF...) qui nécessitent des coûts d'infrastructure (API OpenAI, stockage). 9,90€ = paiement unique, accès à vie."
              },
              {
                question: "C'est juste un rapport PDF ou un vrai outil ?",
                answer: "C'est un tableau de bord interactif accessible en ligne. Vous pouvez simuler différents scénarios d'amélioration, marquer vos actions comme réalisées, suivre votre score dans le temps avec un graphique d'évolution. Le PDF téléchargeable est inclus, mais l'outil principal est en ligne et mis à jour en temps réel."
              },
              {
                question: "Je peux mettre à jour mon score après avoir fait des travaux ?",
                answer: "Oui, absolument ! C'est justement l'un des avantages clés. Lorsque vous marquez une action comme 'Réalisée' (ex: installation alarme), votre score se recalcule automatiquement. Un snapshot est créé pour tracer votre progression. Vous pouvez revenir autant de fois que vous voulez."
              },
              {
                question: "Est-ce que ça fonctionne pour les appartements ET les maisons ?",
                answer: "Oui, l'algorithme s'adapte automatiquement. Les appartements et maisons ont des caractéristiques de sécurité différentes, et les recommandations sont contextualisées en fonction du type de logement."
              },
              {
                question: "Que se passe-t-il si les stats de ma commune ne sont pas disponibles ?",
                answer: "Certaines petites communes sont en 'secret statistique' (moins de 5 cambriolages/an). Dans ce cas, nous affichons un message explicatif et utilisons les moyennes départementales pour le calcul."
              },
              {
                question: "Je peux refaire le diagnostic plus tard ?",
                answer: "Oui, sans limite. Une fois le rapport acheté pour une adresse, vous pouvez suivre l'évolution de la sécurisation de votre bien au fil de vos actions. Pour diagnostiquer un nouveau logement (autre adresse), un nouveau paiement sera nécessaire."
              },
              {
                question: "Le paiement est-il vraiment sécurisé ?",
                answer: "Oui, 100% géré par Stripe (leader mondial du paiement en ligne). Nous ne stockons aucune donnée bancaire."
              },
            ].map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="bg-gradient-to-br from-slate-800/40 to-slate-900/40 backdrop-blur-xl border border-white/10 rounded-xl overflow-hidden"
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full px-6 py-5 text-left flex items-center justify-between hover:bg-white/5 transition-colors"
                  aria-expanded={openFaq === index}
                >
                  <span className="text-lg font-semibold text-white pr-4">
                    {faq.question}
                  </span>
                  <ChevronDown
                    size={24}
                    className={`text-blue-400 flex-shrink-0 transition-transform ${
                      openFaq === index ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                {openFaq === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="px-6 pb-5"
                  >
                    <p className="text-slate-300 leading-relaxed">
                      {faq.answer}
                    </p>
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 8: CTA FINAL */}
      <section className="py-20 px-4 bg-gradient-to-br from-blue-900/30 to-slate-900/30 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 to-purple-600/10" />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white leading-tight">
              Commencez par votre diagnostic gratuit
            </h2>
            <p className="text-xl text-slate-300">
              Score instantané + statistiques locales • Débloquez ensuite votre outil de pilotage pour 9,90€
            </p>
            <div>
              <CTAButton variant="primary" size="xl" icon>
                Obtenir mon score gratuit
              </CTAButton>
            </div>
            <div className="flex flex-wrap justify-center gap-8 pt-4">
              <div className="flex items-center gap-2 text-slate-300">
                <CheckCircle size={20} className="text-green-400" />
                <span>Score gratuit instantané</span>
              </div>
              <div className="flex items-center gap-2 text-slate-300">
                <CheckCircle size={20} className="text-green-400" />
                <span>Statistiques de votre commune</span>
              </div>
              <div className="flex items-center gap-2 text-slate-300">
                <CheckCircle size={20} className="text-green-400" />
                <span>Sans carte bancaire</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
